import { Suspense, lazy } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import DashboardEndClientLayout from '../layouts/dashboard-end-client';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import GuestGuard from '../guards/GuestGuard';

import AuthGuard from '../guards/AuthGuard';
import EndUserAuthGuard from '../guards/EndUserAuthGuard';
import dayjs from 'dayjs';
import { getI18n } from 'react-i18next';
import DashboardHomeEndUser from 'pages/DashboardHomeEndUser';
import AnotherPage from 'pages/AnotherPage';
import DashboardHomeEndClient from 'pages/DashboardHomeEndClient';
import NewDeliver from 'pages/deliver/NewDeliver';
import Onboarding from 'pages/landing/Onboarding';
import DeviceDetailsEndUser from 'pages/devices/DeviceDetailsEndUser';
import { RootState, useSelector } from './../redux/store';
import { checkWorkspace, WORKSPACES_REPSOL, WORKSPACES_AMBAR } from 'guards/CheckRoles';
import ReportListRepsol from 'pages/reports/ReportListRepsol';
import DeliveryConfigurationList from 'pages/DeliveryConfigurations/DeliveryConfigurationList';
import AddDeliveryConfiguration from 'pages/DeliveryConfigurations/AddDeliveryConfiguration';
import EditDeliveryConfiguration from 'pages/DeliveryConfigurations/EditDeliveryConfiguration';
import ProductTypeList from 'pages/ProductType/ProductTypeList';
import AddProductType from 'pages/ProductType/AddProductType';
import ProductScheduleTemplateList from 'pages/ProductScheduleTemplate/ProductScheduleTemplateList';
import AddProductScheduleTemplate from 'pages/ProductScheduleTemplate/AddProductScheduleTemplate';
import DeliveryRequestList from 'pages/DeliveryRequest/DeliveryRequestList';
import EditProductScheduleTemplate from 'pages/ProductScheduleTemplate/EditProductScheduleTemplate';
import ProductScheduleList from 'pages/ProductSchedule/ProductScheduleList';
import AddProductSchedule from 'pages/ProductSchedule/AddProductSchedule';
import EditProductSchedule from 'pages/ProductSchedule/EditProductSchedule';
import NewDeliverSilos from 'pages/deliver/NewDeliverSilos';
import AddBuilding from 'pages/Buildings/AddBuilding';
import BuildingList from 'pages/Buildings/BuildingList';
import EditBuilding from 'pages/Buildings/EditBuilding';
import EditDeviceGroupBuildingDevice from 'pages/DeviceGroups/EditDeviceGroupBuildingDevice';
import ManageOrder from 'pages/landing/ManageOrder';
import GeneralAnalytics from 'pages/CalculatedData/GeneralAnalytics';
import SharedList from 'pages/Shared/SharedList';
import EditForm from 'pages/users/EditForm';

require('dayjs/locale/es');
require('dayjs/locale/en');
require('dayjs/locale/fr');
require('dayjs/locale/de');
require('dayjs/locale/ru');

// ----------------------------------------------------------------------
const Loadable = (Component: any) => (props: any) => {
  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            top: 0,
            left: 0,
            width: 1,
            zIndex: 9999,
            position: 'fixed'
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  dayjs.locale(getI18n().language);
  const { workspace } = useSelector((state: RootState) => state.workspace);
  return useRoutes([
    {
      path: '/landing',
      children: [
        {
          path: 'end-period-report',
          element: <DownloadEndPeriodReport />
        },
        {
          path: 'onboarding/:guid',
          element: (
            <GuestGuard>
              <Onboarding></Onboarding>
            </GuestGuard>
          )
        },
        {
          path: 'manage-order/:guid',
          element: (
            <GuestGuard>
              <ManageOrder></ManageOrder>
            </GuestGuard>
          )
        }
      ]
    },
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        { path: 'login-unprotected', element: <Login /> },
        //{ path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'reset-password-change', element: <ResetPasswordChange /> },
        { path: 'verify', element: <VerifyCode /> }
      ]
    },
    // Dashboard Routes

    {
      path: '/end-user/',
      element: (
        <EndUserAuthGuard>
          <DashboardLayout />
        </EndUserAuthGuard>
      ),
      children: [
        { path: '', element: <DashboardHomeEndUser /> },
        { path: 'another', element: <AnotherPage /> },
        {
          path: 'deliver',
          children: [{ path: 'new', element: <NewDeliverSilos /> }]
        },
        {
          path: 'devices',
          children: [
            { element: <Navigate to="/devices/list" replace /> },
            { path: 'list', element: <DeviceList /> },
            { path: 'details/:guid', element: <DeviceDetailsEndUser /> }
          ]
        },
        {
          path: 'reports',
          children: [
            {
              path: 'list',
              element:
                checkWorkspace(workspace, WORKSPACES_REPSOL) ||
                workspace?.wsName === 'loxamhune' ? (
                  <ReportListRepsol />
                ) : (
                  <ReportList />
                )
            }
          ]
        },
        {
          path: 'alarms',
          children: [
            { element: <Navigate to="/alarms/list" replace /> },
            { path: 'list', element: <AlarmList /> },
            { path: 'new', element: <AddAlarm /> },
            { path: 'edit/:guid', element: <EditAlarm /> },
            { path: 'details/:guid', element: <UnderConstruction /> }
          ]
        },
        {
          path: 'device-groups',
          children: [
            { element: <Navigate to="/device-groups/list" replace /> },
            { path: 'list', element: <DeviceGroupList /> },
            { path: 'new', element: <AddDeviceGroup /> },
            { path: 'edit/:guid', element: <EditDeviceGroup /> }
          ]
        },
        {
          path: 'user',
          children: [{ path: 'settings', element: <EditForm /> }]
        }
      ]
    },
    {
      path: '/end-client/',
      element: (
        <EndUserAuthGuard>
          <DashboardEndClientLayout />
        </EndUserAuthGuard>
      ),
      children: [
        { path: '', element: <DashboardHomeEndClient /> },
        { path: 'another', element: <AnotherPage /> },
        {
          path: 'devices',
          children: [{ path: 'details/:guid', element: <DeviceDetailsEndClient /> }]
        }
      ]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <DashboardHome /> },
        {
          path: 'reports',
          children: [
            {
              path: 'list',
              element:
                checkWorkspace(workspace, WORKSPACES_REPSOL) ||
                workspace?.wsName === 'loxamhune' ? (
                  <ReportListRepsol />
                ) : (
                  <ReportList />
                )
            },
            //{ path: 'new', element: <UnderConstruction /> },
            { path: 'alarm_state_change', element: <AlarmStateChange /> }
          ]
        },
        {
          path: 'devices',
          children: [
            { element: <Navigate to="/devices/list" replace /> },
            { path: 'list', element: <DeviceList /> },
            { path: 'new', element: <AddDevice /> },
            { path: 'labels', element: <LabelList /> },
            { path: 'edit/:guid', element: <EditDevice /> },
            { path: 'details/:guid', element: <DeviceDetails /> }
          ]
        },
        {
          path: 'alarms',
          children: [
            { element: <Navigate to="/alarms/list" replace /> },
            { path: 'list', element: <AlarmList /> },
            { path: 'new', element: <AddAlarm /> },
            { path: 'edit/:guid', element: <EditAlarm /> },
            { path: 'details/:guid', element: <UnderConstruction /> }
          ]
        },
        {
          path: 'assets',
          children: [
            { element: <Navigate to="/assets/list" replace /> },
            { path: 'list', element: <UnderConstruction /> },
            { path: 'new', element: <UnderConstruction /> }
          ]
        },
        {
          path: 'device-groups',
          children: [
            { element: <Navigate to="/device-groups/list" replace /> },
            { path: 'list', element: <DeviceGroupList /> },
            { path: 'new', element: <AddDeviceGroup /> },
            { path: 'edit/:guid', element: <EditDeviceGroup /> }
          ]
        },
        {
          path: 'device-groups-buildings-devices',
          children: [
            { element: <Navigate to="/device-groups-buildings-devices/list" replace /> },
            { path: 'edit/:guid', element: <EditDeviceGroupBuildingDevice /> }
          ]
        },
        {
          path: 'buildings',
          children: [
            { element: <Navigate to="/buildings/list" replace /> },
            { path: 'list', element: <BuildingList /> },
            { path: 'new', element: <AddBuilding /> },
            { path: 'edit/:guid', element: <EditBuilding /> }
          ]
        },
        {
          path: 'delivery-configurations',
          children: [
            { element: <Navigate to="/delivery-configurations/list" replace /> },
            { path: 'list', element: <DeliveryConfigurationList /> },
            { path: 'new', element: <AddDeliveryConfiguration /> },
            { path: 'edit/:guid', element: <EditDeliveryConfiguration /> }
          ]
        },
        {
          path: 'delivery-request',
          children: [
            { element: <Navigate to="/delivery-request/list" replace /> },
            { path: 'list', element: <DeliveryRequestList /> },
            { path: 'new', element: <AddDeliveryConfiguration /> },
            { path: 'edit/:guid', element: <EditDeliveryConfiguration /> }
          ]
        },
        {
          path: 'calculated-data',
          children: [
            { element: <Navigate to="/calculated-data/list" replace /> },
            {
              path: 'list',
              element: checkWorkspace(workspace, WORKSPACES_AMBAR) ? (
                <GeneralAnalytics />
              ) : (
                <UnderConstruction />
              )
            },
            { path: 'new', element: <UnderConstruction /> }
          ]
        },
        {
          path: 'shared',
          children: [
            { element: <Navigate to="/shared/list" replace /> },
            { path: 'list', element: <SharedList /> },
            { path: 'new', element: <UnderConstruction /> }
          ]
        },
        {
          path: 'product-types',
          children: [
            { element: <Navigate to="/product-types/list" replace /> },
            { path: 'list', element: <ProductTypeList /> },
            { path: 'new', element: <AddProductType /> }
            // { path: 'edit/:guid', element: <EditDeliveryConfiguration /> }
          ]
        },
        {
          path: 'product-schedule-template',
          children: [
            { element: <Navigate to="/product-schedule-template/list" replace /> },
            { path: 'list', element: <ProductScheduleTemplateList /> },
            { path: 'new', element: <AddProductScheduleTemplate /> },
            { path: 'edit/:guid', element: <EditProductScheduleTemplate /> }
          ]
        },
        {
          path: 'product-schedule',
          children: [
            { element: <Navigate to="/product-schedule/list" replace /> },
            { path: 'list', element: <ProductScheduleList /> },
            { path: 'new', element: <AddProductSchedule /> },
            { path: 'edit/:guid', element: <EditProductSchedule /> }
          ]
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/user/settings" replace /> },
            {
              path: 'settings',
              element: <EditForm />
            }
          ]
        }
      ]
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [{ element: <LandingPage /> }]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS

//Open paths
const DownloadEndPeriodReport = Loadable(
  lazy(() => import('../pages/landing/DownloadEndPeriodReport'))
);
// User management
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const ResetPasswordChange = Loadable(
  lazy(() => import('../pages/authentication/ResetPasswordChange'))
);
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));

// Reports
const AlarmStateChange = Loadable(
  lazy(() => import('../pages/reports/reports/AlarmStateChangeReport'))
);

const ReportList = Loadable(lazy(() => import('../pages/reports/ReportList')));
const DashboardHome = Loadable(lazy(() => import('../pages/DashboardHome')));
// Device group
const DeviceGroupList = Loadable(lazy(() => import('../pages/DeviceGroups/DeviceGroupList')));
const AddDeviceGroup = Loadable(lazy(() => import('../pages/DeviceGroups/AddDeviceGroup')));
const EditDeviceGroup = Loadable(lazy(() => import('../pages/DeviceGroups/EditDeviceGroup')));

// Devices
const DeviceList = Loadable(lazy(() => import('../pages/devices/DeviceList')));
const DeviceDetails = Loadable(lazy(() => import('../pages/devices/DeviceDetails')));
const EditDevice = Loadable(lazy(() => import('../pages/devices/EditDevice')));
const AddDevice = Loadable(lazy(() => import('../pages/devices/AddDevice')));
const LabelList = Loadable(lazy(() => import('../pages/devices/LabelList')));
// Alarm
const AlarmList = Loadable(lazy(() => import('../pages/alarm/AlarmList')));
const EditAlarm = Loadable(lazy(() => import('../pages/alarm/EditAlarm')));
const AddAlarm = Loadable(lazy(() => import('../pages/alarm/AddAlarm')));

const UnderConstruction = Loadable(lazy(() => import('../pages/UnderConstruction')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// Main
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));

// End Client
// Devices
const DeviceDetailsEndClient = Loadable(
  lazy(() => import('../pages/devices/DeviceDetailsEndClient'))
);
